<template>
  <panel class="case-scenario" title="Scenario" color="blue-6">
    <div class="case-name-language-container">
      <div class="case-name-container">
        <label for="case-name-input">Scenario Name</label>
        <md-field :class="{'md-invalid': hasError('name')}">
          <md-input
            id="case-name-input"
            aria-label="Scenario Name"
            data-testid="scenario-name-input"
            :value="state.caseName"
            :class="{'error': hasError('name')}"
            :maxlength="64"
            :disabled="readonly"
            @input="name => updateName(name)"
          />
          <span v-if="hasError('name')" class="md-error">{{ nameErrors[0].message }}</span>
        </md-field>
      </div>
      <div class="case-timer-container" data-testid="scenario-timer-container">
        <label>Scenario Timer</label>
        <help-tooltip v-if="hasError('timer')" type="error" data-testid="scenario-timer-error">
          Scenario timer must be greater than 0 seconds.
        </help-tooltip>
        <help-tooltip v-else type="info" data-testid="scenario-timer-tooltip">
          Enter how long you want your scenario to run equal to or less than 2 hours.
        </help-tooltip>
        <div class="field-row">
          <md-field class="case-hours-field">
            <md-input
              ref="hoursInput"
              type="number"
              min="0"
              max="2"
              placeholder="hh"
              aria-label="Scenario Timer Hours"
              data-testid="scenario-timer-hours"
              :value="state.caseHours"
              :disabled="readonly"
              @input="updateHours"
            />
          </md-field>
          <span class="field-colon">:</span>
          <md-field class="case-minutes-field">
            <md-input
              id="case-timer-minutes"
              ref="minutesInput"
              type="number"
              min="0"
              max="59"
              placeholder="mm"
              aria-label="Scenario Timer Minutes"
              data-testid="scenario-timer-minutes"
              :value="state.caseMinutes"
              :disabled="readonly"
              @input="updateMinutes"
            />
          </md-field>
          <span class="field-colon">:</span>
          <md-field class="case-seconds-field">
            <md-input
              ref="secondsInput"
              type="number"
              min="0"
              max="59"
              placeholder="ss"
              aria-label="Scenario Timer Seconds"
              data-testid="scenario-timer-seconds"
              :value="state.caseSeconds"
              :disabled="readonly"
              @input="updateSeconds"
            />
          </md-field>
        </div>
      </div>
    </div>

    <accordion
      class="scene-container"
      label="Starting Scene"
      :active="isActivePanel('STARTING_SCENE')"
      @toggle="togglePanel('STARTING_SCENE')"
    >
      <case-scene-builder
        :case-conf="caseConf"
        location="_startLocation"
        :used-info="usedInfo"
        :user="user"
        :readonly="readonly"
        :avatars="avatars"
        :environments="environments"
        :voices="voices"
        @update="update"
      />
    </accordion>

    <div v-if="caseConf._endLocation">
      <accordion
        v-show="caseConf._endLocation"
        class="scene-container"
        label="Ending Scene"
        :active="isActivePanel('ENDING_SCENE')"
        removeable
        @toggle="togglePanel('ENDING_SCENE')"
        @remove="removeEndingScene"
      >
        <case-scene-builder
          :case-conf="caseConf"
          location="_endLocation"
          :used-info="usedInfo"
          :user="user"
          :readonly="readonly"
          :avatars="avatars"
          :environments="environments"
          :voices="voices"
          @update="update"
        />
      </accordion>
    </div>
    <div v-if="!caseConf._endLocation" class="add-scene-row">
      <md-button
        class="md-raised md-primary add-ending-scene-button"
        aria-label="Add Ending Scene"
        data-testid="add-ending-scene-button"
        @click="addEndingScene"
      >
        <md-icon>add</md-icon>Ending Scene
      </md-button>
      <help-tooltip v-if="endLocationWarnings.length" type="warning" direction="bottom" data-testid="ending-scene-tooltip">
        <span v-for="warning in endLocationWarnings" :key="warning.message">
          {{ warning.message }}
          <br>
        </span>
      </help-tooltip>
    </div>
  </panel>
</template>

<script>
import get from 'lodash/get';
import uniqBy from 'lodash/uniqBy';
import cloneDeep from 'lodash/cloneDeep';

import HelpTooltip from '~lib/common/components/HelpTooltip';

import Panel from '../../common/Panel';
import Accordion from '../../common/Accordion/Accordion.vue';
import CaseSceneBuilder from '../CaseSceneBuilder/CaseSceneBuilder.vue';

export default {
  name: 'case-scenario',
  components: {
    CaseSceneBuilder,
    Panel,
    Accordion,
    HelpTooltip,
  },
  props: {
    caseConf: {
      type: Object,
      required: true,
    },
    idToken: {
      type: String,
      required: true,
    },
    readonly: {
      type: Boolean,
    },
    isActive: {
      type: Boolean,
    },
    user: {
      type: Object,
      default: null,
    },
    avatars: {
      type: Array,
      default: () => []
    },
    environments: {
      type: Array,
      default: () => []
    },
    voices: {
      type: Array,
      default: () => []
    },
  },
  data() {
    return {
      state: {
        name: '',
        activeAccordionPanel: 'STARTING_SCENE',
      },
    };
  },
  computed: {
    nameErrors() {
      return this.$store.getters['validation/errorsByField'].name;
    },
    endLocationWarnings() {
      const warnings = this.$store.getters['validation/visibleWarningsByField']._endLocation || [];
      return uniqBy(warnings, 'message');
    },
    usedInfo() {
      const list = [
        {
          key: 'PATIENT',
          avatarKey: this.caseConf._avatar.key,
          voiceKey: this.caseConf.voiceKey,
        }
      ];
      for (const npc of this.caseConf.npcs || []) {
        if (npc.activeScenes && npc.activeScenes.length) {
          list.push({
            key: npc.key,
            avatarKey: npc.avatarKey,
            voiceKey: npc.voiceKey,
          });
        }
      }
      return list;
    },
  },
  created() {
    this.state.caseName = this.caseConf.name;
    this.state.caseHours = get(this.caseConf, 'timer.hours', '');
    this.state.caseMinutes = get(this.caseConf, 'timer.minutes', '15');
    this.state.caseSeconds = get(this.caseConf, 'timer.seconds', '');
    if (!this.caseConf._startLocation) {
      this.$emit('update', {
        _startLocation: this.environments[0]
      });
    }
  },
  methods: {
    updateName(name) {
      // We keep the untrimmed name in the state
      // to prevent the disruption of the user's typing
      this.state.caseName = name;
      const trimmedName = name.trim();
      this.update({ name: trimmedName });
    },
    updateHours(hours) {
      this.state.caseHours = hours;
      this.rangeLimit();
      this.updateTime();
    },
    updateMinutes(minutes) {
      this.state.caseMinutes = minutes;
      this.rangeLimit();
      this.updateTime();
    },
    updateSeconds(seconds) {
      this.state.caseSeconds = seconds;
      this.rangeLimit();
      this.updateTime();
    },
    rangeLimit() {
      const hours = Math.max(0, Math.min(2, this.state.caseHours))
      const minutes = hours === 2 ? 0 : Math.max(0, Math.min(59, this.state.caseMinutes));
      const seconds = hours === 2 ? 0 : Math.max(0, Math.min(59, this.state.caseSeconds));
      if (this.state.caseHours != hours) {
        this.state.caseHours = hours;
        this.$nextTick(() => {
          this.$refs.hoursInput.$el.value = hours;
        });
      }
      if (this.state.caseMinutes != minutes) {
        this.state.caseMinutes = minutes;
        this.$nextTick(() => {
          this.$refs.minutesInput.$el.value = minutes;
        });
      }
      if (this.state.caseSeconds != seconds) {
        this.state.caseSeconds = seconds;
        this.$nextTick(() => {
          this.$refs.secondsInput.$el.value = seconds;
        });
      }
    },
    updateTime() {
      const timer = {
        hours: this.state.caseHours,
        minutes: this.state.caseMinutes,
        seconds: this.state.caseSeconds,
      }
      this.update({ timer });
    },
    update(updates) {
      this.$emit('update', updates);
    },
    hasError(field) {
      return get(this.$store.getters['validation/errorsByField'], field);
    },
    isActivePanel(panel) {
      return this.state.activeAccordionPanel === panel;
    },
    togglePanel(panel) {
      if (this.isActivePanel(panel)) {
        this.state.activeAccordionPanel = null;
      } else {
        this.state.activeAccordionPanel = panel;
      }
    },
    addEndingScene() {
      const updatedNpcs = cloneDeep(this.caseConf.npcs || []);
      const startingNpc = updatedNpcs.find(npc => npc.activeScenes.includes('_startLocation'));
      const endingNpc = updatedNpcs.find(npc => npc.activeScenes.includes('_endLocation'));
      if (startingNpc && !endingNpc) {
        // If no ending npc exists, use the starting NPC for both scenes
        startingNpc.activeScenes = ['_startLocation', '_endLocation'];
      }
      this.$emit('update', {
        _endLocation: this.environments[0],
        npcs: updatedNpcs
      });
      this.$nextTick(() => {
        this.togglePanel('ENDING_SCENE');
      });
    },
    removeEndingScene() {
      // Adjust activeScenes
      let updateNpcs = false;
      for (const npc of this.caseConf.npcs) {
        if (npc.activeScenes.includes('_endLocation')) {
          npc.activeScenes = npc.activeScenes.filter(scene => scene !== '_endLocation')
          updateNpcs = true;
        }
      }
      const updateFields = {
        _endLocation: null
      };
      if (updateNpcs) {
        updateFields.npcs = this.caseConf.npcs;
      }
      this.$emit('update', updateFields);
      if (!this.isActivePanel('STARTING_SCENE')) {
        this.togglePanel('STARTING_SCENE');
      }
    }
  },
};
</script>

<style scoped>
.case-timer-container {
  padding-left: 50px;
  width: 33.3%;
}

.field-row {
  display: flex;
  align-items: center;
}

 .field-colon {
   margin: 0 4px 0 2px;
 }

.case-link-container,
.case-name-language-container {
  margin-top: 45px;
}

.case-name-language-container {
  display: flex;
}

.case-name-language-container {
  margin-bottom: 30px;
}

.case-name-container {
  width: 66.6%;
}

.case-hours-field,
.case-minutes-field,
.case-seconds-field {
  flex: 1;
  margin: 0;
}

.case-hours-field .md-input,
.case-minutes-field .md-input,
.case-seconds-field .md-input {
  text-align: center;
}

.case-name-container .md-field,
.case-timer-container .md-field {
  margin-top: 5px;
}

.help-tooltip.tooltip-icon {
  margin: 0 5px;
}

.add-scene-row {
  text-align: center;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
}

.add-ending-scene-button {
  border-radius: 16px;
}
</style>
